"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, Cable, ChevronRight, MoreVertical, Plus, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { NewInfrastructureLiveSetup } from "./new-infrastructure-live-console";

type Props={
  setups:NewInfrastructureLiveSetup[];
  selected:NewInfrastructureLiveSetup|null;
  selectedSetupId:string|null;
  missingSelection:boolean;
  higherPriorityOverlayOpen:boolean;
  open:boolean;
  onOpenChange:(next:boolean)=>void;
  onSelect:(setupId:string|null)=>void;
  onOpenSetup:(setupId:string)=>void;
  onRefresh:()=>void;
  onMarketplace:()=>void;
  onAddMember:()=>void;
  onRegisterExisting:()=>void;
  busy:boolean;
};

export function ManualMembersWorkspace({
  setups,selected,selectedSetupId,missingSelection,higherPriorityOverlayOpen,open,onOpenChange,onSelect,
  onOpenSetup,onRefresh,onMarketplace,onAddMember,onRegisterExisting,busy,
}:Props) {
  const [search,setSearch]=useState("");
  const [menuId,setMenuId]=useState<string|null>(null);
  useEffect(()=>{
    // Infrastructure workspace list and three-dot menus are local navigation layers.
    // System Details and other higher-priority dialogs must consume Back first.
    if(higherPriorityOverlayOpen||(!menuId&&!open)||typeof window==="undefined") return;
    const closeLocal=(event:Event)=>{
      event.preventDefault();
      event.stopImmediatePropagation();
      if(menuId) setMenuId(null);
      else onOpenChange(false);
    };
    const escape=(event:KeyboardEvent)=>{
      if(event.key==="Escape") closeLocal(event);
    };
    window.addEventListener("runyi:request-back",closeLocal as EventListener,{capture:true});
    window.addEventListener("keydown",escape);
    return()=>{
      window.removeEventListener("runyi:request-back",closeLocal as EventListener,{capture:true});
      window.removeEventListener("keydown",escape);
    };
  },[menuId,open,onOpenChange,higherPriorityOverlayOpen]);
  // Every workspace is resolved from the live RUNYI setup registry. Provider-only
  // capabilities remain discoverable even when they have no member or project reference.
  const workspaces=[...setups]
    .sort((a,b)=>a.display_name.localeCompare(b.display_name,undefined,{numeric:true}));
  const visible=workspaces.filter(item=>
    [
      item.display_name,
      item.target_member_email,
      item.target_account_ref,
      item.target_project_ref,
      item.provider_key,
      item.provider_capability_key,
      item.profile?.logical_capability,
      item.capability?.service_type,
      item.backend_stage,
    ].join(" ").toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const select=(id:string|null)=>{
    setMenuId(null);
    onSelect(id);
    onOpenChange(id===null);
  };
  const pct=(item:NewInfrastructureLiveSetup)=>Math.round(item.digital_display?.progress_percent??item.progress_percent);
  const state=(item:NewInfrastructureLiveSetup)=>pct(item)===100?"ACTIVE":item.backend_stage.replaceAll("_"," ");

  return (
    <section aria-label="Infrastructure Workspaces" className="w-full min-w-0 rounded-[18px] border border-lime-400/25 bg-[#111510] p-3 text-white">
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/45">Selected Infrastructure Workspace</p>
          <h2 className="mt-1 truncate text-base font-semibold text-lime-300">{selected?.display_name??"All Infrastructure"}</h2>
          {selected&&<p className="mt-0.5 break-all text-[11px] text-white/70">{selected.target_member_email??selected.target_account_ref??selected.provider_key}</p>}
          {selected?.target_project_ref&&<p className="mt-0.5 break-all font-mono text-[9px] text-white/40">{selected.target_project_ref}</p>}
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-1 text-[9px] font-bold ${selected?"border-lime-400/35 bg-lime-400/10 text-lime-300":"border-white/20 text-white/60"}`}>
          {selected?`${pct(selected)}% · ${state(selected)}`:`${workspaces.length} WORKSPACES`}
        </span>
      </div>
      {missingSelection&&<p role="status" className="mt-2 rounded-lg border border-amber-400/30 px-2.5 py-2 text-[10px] text-amber-200">This selected infrastructure is not in the current authorized registry. No other member has been silently selected. Choose a member below.</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" className="min-h-11 flex-1 rounded-xl border-lime-400/30 bg-lime-500/10 text-lime-200 hover:bg-lime-500/20" onClick={()=>onOpenChange(!open)} aria-expanded={open} aria-controls="runyi-infrastructure-workspaces">
          <Users className="size-4"/> Infrastructure ({workspaces.length}) <ChevronRight className={`ml-auto size-4 transition-transform ${open?"rotate-90":""}`}/>
        </Button>
        <Button type="button" size="sm" variant="outline" className="min-h-11 rounded-xl border-white/20 bg-white/[0.04] text-white/80" onClick={()=>select(null)}><ArrowLeft className="size-3.5"/> All</Button>
      </div>
      {selected&&<div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[10px]">
        <span className="text-white/60">{selected.provider_key} · {state(selected)}</span>
        <button type="button" className="min-h-10 rounded-lg border border-lime-400/25 px-3 font-semibold text-lime-200" onClick={()=>onOpenSetup(selected.setup_id)} disabled={busy}>Open Workspace <ChevronRight className="inline size-3.5"/></button>
      </div>}
      {open&&<div id="runyi-infrastructure-workspaces" className="mt-3 space-y-2 border-t border-white/10 pt-3">
        <label htmlFor="runyi-infrastructure-search" className="block text-[10px] font-semibold text-white/70">Search by provider, account, project or capability</label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 size-4 text-white/40"/>
          <Input id="runyi-infrastructure-search" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search infrastructure workspaces…" className="min-h-11 border-white/20 bg-white/[0.05] pl-10 text-base text-white placeholder:text-white/40"/>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" className="min-h-11 flex-1 rounded-xl bg-lime-400 font-semibold text-black hover:bg-lime-300" onClick={onAddMember}><Plus className="size-4"/> Add Member</Button>
          <Button type="button" size="sm" variant="outline" className="min-h-11 flex-1 rounded-xl border-white/20 bg-white/[0.05] text-white hover:bg-white/[0.09]" onClick={onRegisterExisting}><Cable className="size-4"/> Register Existing</Button>
        </div>
        {visible.length===0
          ? <p className="rounded-xl border border-dashed border-white/15 p-3 text-xs text-white/60">{workspaces.length?"No infrastructure workspaces match your search.":"No infrastructure workspaces registered yet."}</p>
          : <div className="max-h-[min(55dvh,540px)] space-y-2 overflow-y-auto overscroll-contain pb-1">
            {visible.map(item=>{
              const active=item.setup_id===selectedSetupId;
              const menuOpen=menuId===item.setup_id;
              return <div key={item.setup_id} className={`min-w-0 rounded-xl border ${active?"border-lime-400/60 bg-lime-400/[0.07]":"border-white/15 bg-white/[0.035]"}`}>
                <div className="flex min-w-0 items-center gap-2 p-2">
                  <button type="button" aria-pressed={active} className="min-w-0 flex-1 rounded-lg p-1.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-lime-400" onClick={()=>select(item.setup_id)}>
                    <span className="flex items-center gap-2 text-[12px] font-semibold text-white"><span className="truncate">{item.display_name}</span>{active&&<span className="shrink-0 text-[9px] text-lime-300">SELECTED</span>}</span>
                    <span className="mt-1 block break-all text-[10px] text-white/65">{item.target_member_email??item.target_account_ref??item.provider_key}</span>
                    <span className="mt-1 block break-all font-mono text-[9px] text-white/40">{item.target_project_ref??item.profile?.logical_capability??item.capability?.service_type??"Provider setup"}</span>
                    <span className="mt-1 block text-[9px] font-medium text-lime-300">{pct(item)}% · {state(item)}</span>
                  </button>
                  <button type="button" aria-expanded={menuOpen} aria-label={`Manage ${item.display_name}`} title={`Manage ${item.display_name}`} onClick={()=>setMenuId(old=>old===item.setup_id?null:item.setup_id)} className="grid size-10 shrink-0 place-items-center rounded-xl border border-white/15 text-white/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lime-400"><MoreVertical className="size-4"/></button>
                </div>
                {menuOpen&&<div className="grid gap-1 border-t border-white/10 p-2">
                  <button type="button" className="min-h-10 rounded-lg px-3 text-left text-[11px] text-white hover:bg-white/10" onClick={()=>{select(item.setup_id);onOpenSetup(item.setup_id);}}>Open Workspace / Edit Configuration</button>
                  <button type="button" className="min-h-10 rounded-lg px-3 text-left text-[11px] text-white hover:bg-white/10" onClick={()=>{select(item.setup_id);onRefresh();}}>Refresh Connection & Health</button>
                  <button type="button" className="min-h-10 rounded-lg px-3 text-left text-[11px] text-white hover:bg-white/10" onClick={()=>{select(item.setup_id);onOpenSetup(item.setup_id);}}>Authentication & Installation History</button>
                  <button type="button" className="min-h-10 rounded-lg px-3 text-left text-[11px] text-white hover:bg-white/10" onClick={()=>{select(item.setup_id);onMarketplace();}}>Browse Marketplace</button>
                  <p className="px-3 py-1 text-[9px] text-white/40">Removal is unavailable until the protected backend removal workflow exists. No destructive action happens here.</p>
                </div>}
              </div>;
            })}
          </div>}
      </div>}
    </section>
  );
}
